<template>
  <div>
    <QPageContainer>
      <QPage>
        <section>
          <ActivationPermanent :has-active-subscription="hasActiveSubscription" :demand-recap="demandRecap" :recruitment="recruitment" />
        </section>
      </QPage>
    </QPageContainer>

    <QFooter class="bg-transparent padding-0-15">
      <div class="margin-15-0">
        <FooterBtnsWrapper>
          <ExtraBtnIcon
            color="secondary"
            class="full-width"
            type="button"
            no-caps
            :all-space="true"
            icon-name="icon-extra-magic"
            :btn-label="actionLabel"
            @click="onPayAction"
          />
        </FooterBtnsWrapper>
      </div>
    </QFooter>

    <CardsListModal
      v-if="cardsCollection.hasCards()"
      v-model:opened="paymentModalOpened"
      :cards-collection="cardsCollection"
      @on-add-card="onAddCard"
      @on-select-and-pay="onSelectAndPay"
    />
  </div>
</template>

<script>
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { RecruitmentsService, EtabProfileService } from "@core/services";
  import { ETAB_ROUTES } from "@etab/config";
  import { useRecruitmentStore } from "@etab/stores/recruitment";
  import { mapState } from "pinia";
  import { GETTER_RECRUITMENT_GET_CURRENT } from "@etab/stores/types";
  import { DemandRecapModel, CardsCollectionModel } from "@core/models";
  import { paymentMixin } from "@etab/mixins";

  import CardsListModal from "@etab/components/payment/CardsList.modal.vue";
  import ActivationPermanent from "@etab/components/payment/ActivationPermanent.component.vue";
  export default {
    components: {
      CardsListModal,
      ActivationPermanent,
    },
    mixins: [paymentMixin],
    data() {
      return {
        paymentModalOpened: false,
        hasActiveSubscription: false,
        demandRecap: new DemandRecapModel(),
        cardsCollection: new CardsCollectionModel(),
      };
    },
    computed: {
      ...mapState(useRecruitmentStore, {
        recruitment: GETTER_RECRUITMENT_GET_CURRENT,
      }),
      demandId() {
        return this.$route.params.demandId;
      },
      demandType() {
        return this.$route.params.demandType;
      },
      extraId() {
        return this.$route.query.extraId;
      },
      canUseCredit() {
        return this.demandRecap.canUseCredit();
      },
      actionLabel() {
        if (this.hasActiveSubscription) {
          return this.$t("ETAB.PAGES.RECRUITMENT.ACTIVATION.@ACTION_SUBSCRIPTION");
        }
        if (this.canUseCredit) {
          return this.$t("ETAB.PAGES.RECRUITMENT.ACTIVATION.@ACTION_CREDIT");
        }
        return this.$t("ETAB.PAGES.RECRUITMENT.ACTIVATION.@ACTION_CARD");
      },
    },
    created() {
      this.getData();
    },
    methods: {
      async getData() {
        try {
          this.$_startGlobalLoading();
          if (this.demandId) {
            const [demandRecap, hasActiveSubscription] = await Promise.all([
              RecruitmentsService.getRecap({ demandId: this.demandId }),
              EtabProfileService.hasActiveSubscription(),
            ]);
            this.demandRecap = new DemandRecapModel(demandRecap);
            this.hasActiveSubscription = hasActiveSubscription;
          } else {
            NotificationsManager.error();
          }
        } catch (error) {
          NotificationsManager.error();
          throw error;
        } finally {
          this.$_stopGlobalLoading();
        }
      },
      async onPayAction() {
        try {
          this.$_startGlobalLoading();
          const paymentDispensed = await this.attemptPaymentDispense();
          if (paymentDispensed) {
            this.goToSuccess();
          } else {
            await this.chooseCard();
          }
        } catch (error) {
          NotificationsManager.error();
          throw error;
        } finally {
          this.$_stopGlobalLoading();
        }
      },
      async attemptPaymentDispense() {
        const demandId = this.demandId;
        const extraId = this.extraId;
        // if the etab is subscribed, he doesn't pay
        if (this.hasActiveSubscription) {
          await this.$_unlockWithSubscription({ demandId, extraId });
          return true;
        }
        // if there's credits, we use them to pay
        if (this.canUseCredit) {
          await this.$_payWithCredits({ demandId, extraId });
          return true;
        }
        return false;
      },
      async chooseCard() {
        const cardsCollection = await this.$_getSavedCards();
        if (cardsCollection.hasCards()) {
          this.cardsCollection = cardsCollection;
          this.openPaymentModal();
        } else {
          this.goToAddCardAndPay();
        }
      },
      openPaymentModal() {
        this.paymentModalOpened = true;
      },
      closePaymentModal() {
        this.paymentModalOpened = false;
      },
      goToAddCardAndPay() {
        this.$router.replace({
          name: ETAB_ROUTES.payment.addAndPay.name,
          query: { demandId: this.demandId, demandType: this.demandType, extraId: this.extraId },
        });
      },
      onAddCard() {
        this.closePaymentModal();
        this.goToAddCardAndPay();
      },
      async onSelectAndPay({ selectedCard }) {
        try {
          const cardId = selectedCard.getId();
          const demandId = this.demandId;
          // RG: Permanent Recruitment Activation - upfront is 0 when we pay after the creation of the demand
          const upfront = 0;

          this.$_startGlobalLoading();

          await this.$_payWithCard({ cardId, demandId, upfront });

          this.closePaymentModal();
          this.goToSuccess();
        } catch (error) {
          this.$_handlePaymentError({ error });
        } finally {
          this.$_stopGlobalLoading();
        }
      },
      goToSuccess() {
        this.$router.replace({
          name: ETAB_ROUTES.dashboard.recruitment.permanent.activation.success.name,
          params: { demandId: this.demandId, demandType: this.demandType },
          query: { extraId: this.extraId },
        });
      },
    },
  };
</script>
